// apps/web/src/pages/vendas/detalhe/pendencias-badges.tsx — Fase 12, Plano 21 (PV-05, PV-06).
// Badges de pendência do item: até 2 na linha; o restante vira "+N" e abre um Popover por clique/toque
// (nunca depende de hover). Rose = impede um passo do fluxo; amber = atenção sem impedir.
import type { ComponentType } from 'react';
import { CircleDollarSign, Link2Off, Route, TrendingUp } from 'lucide-react';
import type { PendenciaItemPv } from '@pieltec/contracts';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { rotuloPendenciaPv, type IconePendenciaPv } from '../../../lib/vendas/apresentacao';

const MAXIMO_VISIVEL = 2;

const ICONES: Record<IconePendenciaPv, ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>> = {
  Link2Off,
  CircleDollarSign,
  TrendingUp,
  Route,
};

export function PendenciaBadge({ pendencia }: { pendencia: PendenciaItemPv }) {
  const rotulo = rotuloPendenciaPv(pendencia.codigo);
  const Icone = ICONES[rotulo.icone];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${rotulo.classes}`}
      title={pendencia.detalhe ?? rotulo.apoio ?? undefined}
    >
      <Icone className="size-3 shrink-0" aria-hidden="true" />
      {rotulo.texto}
      {rotulo.apoio && <span className="font-normal">· {rotulo.apoio}</span>}
    </span>
  );
}

export function PendenciasBadges({ pendencias }: { pendencias: readonly PendenciaItemPv[] }) {
  if (pendencias.length === 0) return <span className="text-muted-foreground">—</span>;

  const visiveis = pendencias.slice(0, MAXIMO_VISIVEL);
  const restantes = pendencias.length - visiveis.length;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visiveis.map((pendencia) => (
        <PendenciaBadge key={pendencia.codigo} pendencia={pendencia} />
      ))}
      {restantes > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={`Ver todas as ${pendencias.length} pendências`}
              className="inline-flex min-h-7 min-w-7 items-center justify-center rounded-full border border-slate-200 bg-white px-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              +{restantes}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Pendências do item ({pendencias.length})
            </p>
            <ul className="flex flex-col gap-2">
              {pendencias.map((pendencia) => (
                <li key={pendencia.codigo} className="flex flex-col gap-0.5">
                  <PendenciaBadge pendencia={pendencia} />
                  {pendencia.detalhe && <span className="text-xs text-muted-foreground">{pendencia.detalhe}</span>}
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
